import { useState } from "react";

import { useAddLanguageToSurvey } from "../../../hooks/mutations/admin/surveyTranslations";
import { useSurveyLanguages } from "../../../hooks/queries/admin/surveyTranslations";
import { useToast } from "../../../hooks/useToast";
import { errorToastMessage } from "../../../utils/HttpError";
import {
  AdminSelectField,
  FieldHint,
  FilterButton,
  FilterGroup,
  FilterLabel,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
} from "../shared";
import { ModalBodyText, ResultNotice } from "./AdminTranslations.styles";

interface IAddSurveyLanguageModalProps {
  surveyId: string;
  surveyName: string;
  /** Languages the survey already has, so they can be excluded. */
  existingCodes: string[];
  onClose: () => void;
}

/**
 * Add a language to a survey — Retool's `modalAddNewLanguage` (EN-5163).
 *
 * The endpoint seeds a copy of the survey's strings in the chosen language and
 * returns `{ totalCreated, totalSkipped }`. Retool threw that away and showed
 * a flat "The new survey language was added successfully." — the counts are
 * the answer to "did it do anything?", so the modal stays open and reports
 * them (same call as `SyncTenantIdsModal`, EN-5152).
 */
export function AddSurveyLanguageModal({
  surveyId,
  surveyName,
  existingCodes,
  onClose,
}: IAddSurveyLanguageModalProps) {
  const { showToast } = useToast();
  const { languages, isLoading } = useSurveyLanguages();
  const { addLanguageToSurvey, isAddingLanguage } =
    useAddLanguageToSurvey(surveyId);

  const [languageCode, setLanguageCode] = useState("");
  const [result, setResult] = useState<{
    created: number;
    skipped: number;
  } | null>(null);

  // A language the survey already has would only ever report "all skipped".
  const available = languages.filter(
    (language) => !existingCodes.includes(language.code)
  );

  const handleAdd = async () => {
    if (!languageCode) return;

    try {
      const outcome = await addLanguageToSurvey(languageCode);
      setResult({
        created: outcome?.totalCreated ?? 0,
        skipped: outcome?.totalSkipped ?? 0,
      });
      showToast("The new survey language was added successfully.", "success");
    } catch (error) {
      showToast(
        errorToastMessage(error, "There was an error adding this language."),
        "warning"
      );
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>Add a language</ModalTitle>

      <ModalForm>
        <ModalBodyText>
          Seeds a copy of <strong>{surveyName}</strong>&apos;s strings in the
          chosen language, ready to be translated.
        </ModalBodyText>

        <FilterGroup>
          <FilterLabel htmlFor="survey-language">Language</FilterLabel>
          <AdminSelectField
            id="survey-language"
            value={languageCode}
            disabled={isLoading || isAddingLanguage || result !== null}
            onChange={(e) => setLanguageCode(e.target.value)}
          >
            <option value="">
              {isLoading ? "Loading..." : "Select a language"}
            </option>
            {available.map((language) => (
              <option key={language.code} value={language.code}>
                {language.name} ({language.code})
              </option>
            ))}
          </AdminSelectField>
          {!isLoading && available.length === 0 && (
            <FieldHint>
              This survey already has every language in the catalogue.
            </FieldHint>
          )}
        </FilterGroup>

        {result && (
          <ResultNotice>
            {result.created} string{result.created === 1 ? "" : "s"} created
            {result.skipped > 0
              ? `, ${result.skipped} already present and left alone.`
              : "."}
          </ResultNotice>
        )}

        <ModalFormActions>
          <FilterButton
            variant={result ? "primary" : "secondary"}
            disabled={isAddingLanguage}
            onClick={onClose}
          >
            {result ? "Done" : "Cancel"}
          </FilterButton>
          {!result && (
            <FilterButton
              variant="primary"
              disabled={isAddingLanguage || !languageCode}
              onClick={handleAdd}
            >
              {isAddingLanguage ? "Adding..." : "Add language"}
            </FilterButton>
          )}
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
